import { useState } from 'react'

export type SortValue = string | number | null | undefined

interface SortState<TKey extends string> {
  key: TKey
  desc: boolean
}

/** Text in Norwegian order, numbers by size; empty values always last. */
export const sortRows = <TRow>(
  rows: TRow[],
  value: (row: TRow) => SortValue,
  desc: boolean
): TRow[] =>
  [...rows].sort((a, b) => {
    const left = value(a)
    const right = value(b)
    if (left === right) return 0
    if (left === null || left === undefined || left === '') return 1
    if (right === null || right === undefined || right === '') return -1
    const order =
      typeof left === 'number' && typeof right === 'number'
        ? left - right
        : String(left).localeCompare(String(right), 'nb')
    return desc ? -order : order
  })

/**
 * Sorting for hand-built tables: the rows in the current order, and the props
 * for a sortable `TableHead`. A first click sorts numbers from the largest and
 * text from A; another click on the same column flips it.
 */
export const useRowSort = <TRow, TKey extends string>(
  rows: TRow[],
  values: Record<TKey, (row: TRow) => SortValue>,
  initial: SortState<TKey> | null = null
) => {
  const [sort, setSort] = useState<SortState<TKey> | null>(initial)

  const sorted = sort ? sortRows(rows, values[sort.key], sort.desc) : rows

  const headProps = (key: TKey) =>
    ({
      sortable: true,
      sortDir: sort?.key === key ? (sort.desc ? 'desc' : 'asc') : false,
      onClick: () =>
        setSort(previous => {
          if (previous?.key === key) return { key, desc: !previous.desc }
          const sample = rows
            .map(values[key])
            .find(v => v !== null && v !== undefined)
          return { key, desc: typeof sample === 'number' }
        }),
    }) as const

  return { sorted, headProps }
}
